import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { FunnelBars } from '../../components/charts/FunnelBars'
import { LineChart } from '../../components/charts/LineChart'
import { StackedBar, type StackedPart } from '../../components/charts/StackedBar'
import { visitorsApi, type Revenue, type Step } from '../../api/visitors'
import { fmtDate, fmtInt, PUBLICATION_NAMES } from '../../lib/format'
import { cents, delta, duration, errorDetail, rate, STEP_LABEL } from '../../lib/visitorsFormat'
import { presetRange, todayEastern, type DateRange } from '../../lib/visitorsRange'
import type { Query } from '@tanstack/react-query'
import { HeatGrid } from '../../components/charts/HeatGrid'
import { cohortCell, reasonLabel } from '../../lib/visitorsEngagement'
import { COLLECTING_TEXT, useMemberAccess } from './engagementBits'
import { ScoreDayNote } from './ScoreDayNote'

const poll = (ms: number) => (q: Query<any, any, any, any>) => (q.state.status === 'error' ? false : ms) // eslint-disable-line @typescript-eslint/no-explicit-any
const STEPS: Step[] = ['visit', 'engaged', 'considered', 'checkout', 'paid']
const PAY_LABEL: Record<string, string> = { card: 'Card', bank: 'eCheck', paypal: 'PayPal', Check: 'Cheque' }
const PAY_TONE: StackedPart['tone'][] = ['amber', 'cyan', 'violet', 'green', 'faint']
const payLabel = (k: string) => PAY_LABEL[k] ?? (k ? k.charAt(0).toUpperCase() + k.slice(1) : k)
const TONE_CLASS = { green: 'text-radar-green', red: 'text-radar-red', faint: 'text-paper-faint' } as const

/** App tile caption: views and share, then the top 3 click placements (tooltip lists all). */
function appSub(d: { guide_views: number; clicks_by_placement: Record<string, number>; app_share: number }) {
  const all = Object.entries(d.clicks_by_placement).sort((a, b) => b[1] - a[1])
  const line = `${rate(d.app_share)} of member reading · ${fmtInt(d.guide_views)} /app views`
  if (!all.length) return line
  const fmt = (rows: [string, number][]) => rows.map(([p, n]) => `${p} ${fmtInt(n)}`).join(' · ')
  return <span title={`Clicks by placement: ${fmt(all)}`}>{line}<br />{fmt(all.slice(0, 3))}</span>
}

function alsoInRange(r: Revenue): string | null {
  const parts: string[] = []
  if (r.renewals.orders > 0) parts.push(`renewals ${fmtInt(r.renewals.orders)} · ${cents(r.renewals.cents)}`)
  if (r.offline.orders > 0) parts.push(`offline/cheque ${fmtInt(r.offline.orders)} · ${cents(r.offline.cents)}`)
  return parts.length ? `Also in range: ${parts.join(' · ')}` : null
}

const th = 'label text-left font-normal px-3 py-2'
const tdn = 'px-3 py-2 text-right'

/** The Revenue tab: the former title page body (revenue, member engagement,
 *  funnel, email, visitors per day). `search` is the old-style range query
 *  (`range=custom&from=…&to=…`) the drill-down pages still read. */
export function VisitorsRevenueBody({ pub, range, search }: { pub: string; range: DateRange; search: string }) {
  const valid = Object.hasOwn(PUBLICATION_NAMES, pub)
  const key = [pub, range.from, range.to]
  const summary = useQuery({ queryKey: ['visitors-summary', ...key], queryFn: () => visitorsApi.summary(pub, range), enabled: valid, refetchInterval: poll(60_000) })
  const series = useQuery({ queryKey: ['visitors-timeseries', ...key], queryFn: () => visitorsApi.timeseries(pub, range), enabled: valid, refetchInterval: poll(60_000) })
  const channels = useQuery({ queryKey: ['visitors-breakdown', ...key, 'channel'], queryFn: () => visitorsApi.breakdown(pub, range, 'channel'), enabled: valid })
  const emails = useQuery({ queryKey: ['visitors-emails', ...key], queryFn: () => visitorsApi.emails(pub, range), enabled: valid, retry: false })

  const app = useQuery({ queryKey: ['visitors-app', ...key], queryFn: () => visitorsApi.app(pub, range), enabled: valid, retry: false })
  const titles = useQuery({ queryKey: ['visitors-titles'], queryFn: () => visitorsApi.titles(), enabled: valid, staleTime: 60_000 })
  const { canMember, loaded: meLoaded } = useMemberAccess()
  const eng = useQuery({ queryKey: ['visitors-engagement', ...key], queryFn: () => visitorsApi.engagement(pub, range), enabled: valid, retry: false })
  const dls = useQuery({ queryKey: ['visitors-downloads', ...key, null], queryFn: () => visitorsApi.downloads(pub, range), enabled: valid, retry: false })
  const cohorts = useQuery({ queryKey: ['visitors-cohorts', pub], queryFn: () => visitorsApi.cohorts(pub), enabled: valid, retry: false })
  const atRisk = useQuery({ queryKey: ['visitors-at-risk', pub, 30, 'any', 0, 'preview'], queryFn: () => visitorsApi.atRisk(pub, 30, 'any', 5, 0), enabled: valid && canMember, retry: false })

  const base = `/visitors/${pub}`
  const s = summary.data

  const failed = [summary, series, channels].find((q) => q.isError)
  if (summary.isError) {
    return <Panel accent="red"><p className="font-mono text-[12px] text-radar-red">{errorDetail(summary.error)}</p></Panel>
  }
  if (!s) {
    return (
      <div className="space-y-6">
        <div className="h-64 bg-ink-800 animate-pulse" />
        <div className="h-48 bg-ink-800 animate-pulse" />
      </div>
    )
  }

  const rev = s.revenue
  const funnel = STEPS.map((step, i) => {
    const row = s.funnel.find((f) => f.step === step)
    const value = row?.sessions ?? 0
    let sub = ''
    if (step === 'visit') {
      const prev = s.previous.funnel.find((f) => f.step === 'visit')?.sessions ?? 0
      sub = `${delta(value, prev).text} vs prev`
    } else if (row?.rate_from_previous != null) {
      sub = `${rate(row.rate_from_previous)} of ${STEP_LABEL[STEPS[i - 1]].toLowerCase()}`
    }
    return {
      label: STEP_LABEL[step],
      value,
      sub,
      href: step === 'visit' ? `${base}/sessions?${search}` : `${base}/funnel/${step}?${search}`,
    }
  })

  const payParts: StackedPart[] = Object.entries(rev.by_payment)
    .filter(([, v]) => v > 0)
    .map(([k, v], i) => ({ label: payLabel(k), value: v, tone: PAY_TONE[i % PAY_TONE.length] }))
  const extra = alsoInRange(rev)
  const t0 = titles.data?.find((x) => x.slug === pub)?.first_event_at ?? null

  const days = series.data?.days ?? []
  const prevVals = days.map((_, i) => series.data?.previous[i]?.visitors ?? 0)
  const t = s.traffic
  const pt = s.previous.traffic
  const vd = delta(t.visitors, pt.visitors)

  return (
    <div className="space-y-6">
      {failed && (
        <Panel accent="red"><p className="font-mono text-[12px] text-radar-red">{errorDetail(failed.error)}</p></Panel>
      )}

      <Panel label="Revenue" accent="amber">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
          <div className="bg-ink-900 p-4">
            <Metric size="sm" label="New members" value={rev.new_members} sub={`${fmtInt(rev.gifts)} gift · ${fmtInt(rev.promo_orders)} promo`} />
          </div>
          <div className="bg-ink-900 p-4">
            <Metric size="sm" tone="amber" label="Net revenue" value={cents(rev.net_cents)} sub={`gross ${cents(rev.gross_cents)} · refunds ${cents(rev.refunds_cents)}`} />
          </div>
          <div className="bg-ink-900 p-4">
            <Metric size="sm" label="Avg order" value={cents(rev.aov_cents)} />
          </div>
          <div className="bg-ink-900 p-4 flex flex-col gap-2">
            <span className="label">Payment mix</span>
            {payParts.length ? <StackedBar parts={payParts} ariaLabel="Payment mix" /> : <span className="font-mono text-[12px] text-paper-faint">—</span>}
          </div>
        </div>
        {extra && <p className="mt-2 font-mono text-[11px] text-paper-faint">{extra}</p>}
      </Panel>

      <Panel label="Member engagement" accent="violet">
        {eng.isError ? (
          <p className="font-mono text-[12px] text-radar-red">{errorDetail(eng.error)}</p>
        ) : !eng.data ? (
          <div className="h-24 bg-ink-800 animate-pulse" />
        ) : (
          <div className="space-y-4">
            {eng.data.collecting && (
              <p className="text-paper">{t0 ? `${COLLECTING_TEXT} (since ${fmtDate(t0)})` : 'Collecting — no data yet'}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
              <Metric size="sm" label="Active 30d" value={eng.data.active_30d} sub={`of ${fmtInt(eng.data.members)} · ${rate(eng.data.active_share_30d)}`} />
              <Metric size="sm" label="Active 7d" value={eng.data.active_7d} />
              <Metric size="sm" label="Active today" value={eng.data.active_1d} />
              <Metric size="sm" label="Reads per active" value={eng.data.reads_per_active} sub={`median depth ${Math.round(eng.data.median_depth)}%`} />
              <Metric size="sm" label="Edition opens" value={eng.data.edition_opens} sub={`${fmtInt(eng.data.edition_pages)} pages`} />
              <Link to={`${base}/downloads?${search}`} className="block hover:bg-ink-700 -m-1 p-1" aria-label="Issue downloads, details">
                <Metric size="sm" label="Issue downloads" value={dls.data ? dls.data.downloads : '—'}
                        sub={dls.data ? `${fmtInt(dls.data.downloaders)} downloaders · details →` : 'details →'} />
              </Link>
              <Metric size="sm" label="App" value={app.data ? app.data.app_readers : '—'}
                      sub={app.data ? appSub(app.data) : undefined} />
              <Metric size="sm" label="Sign-ins" value={eng.data.sign_ins.password + eng.data.sign_ins.magic_link + eng.data.sign_ins.account_zip + eng.data.sign_ins.sign_in_code}
                      sub={<>all titles<br />link {fmtInt(eng.data.sign_ins.magic_link)} · code {fmtInt(eng.data.sign_ins.sign_in_code)} · password {fmtInt(eng.data.sign_ins.password)} · account+ZIP {fmtInt(eng.data.sign_ins.account_zip)}</>} />
            </div>
            {!eng.data.collecting && <ScoreDayNote scoreDay={eng.data.score_day} />}
            {canMember ? (
              <div>
                <h3 className="label mb-2">At risk, renewing within 30 days</h3>
                {atRisk.isError ? (
                  <p className="font-mono text-[12px] text-radar-red">{errorDetail(atRisk.error)}</p>
                ) : !atRisk.data ? (
                  <div className="h-16 bg-ink-800 animate-pulse" />
                ) : atRisk.data.collecting || atRisk.data.score_day === null ? (
                  <p className="font-mono text-[12px] text-paper-faint">Collecting — no at-risk list yet</p>
                ) : (
                  <>
                    <ul className="font-mono text-[12px] text-paper">
                      {atRisk.data.items.map((r) => (
                        <li key={r.subscriber_id} className="py-1">
                          <Link to={`${base}/members/${r.subscriber_id}?${search}`} className="text-amber-glow hover:underline">#{r.subscriber_id}</Link>
                          {' · '}renews {fmtDate(r.renews_on)} · score {r.score ?? '—'} · {reasonLabel(r.reason)}
                        </li>
                      ))}
                      {atRisk.data.items.length === 0 && <li className="text-paper-faint">No one at risk in this window</li>}
                    </ul>
                    <Link to={`${base}/at-risk?${search}`} className="font-mono text-[12px] text-amber-glow hover:underline">View all {fmtInt(atRisk.data.total)} →</Link>
                  </>
                )}
              </div>
            ) : meLoaded ? (
              <p className="font-mono text-[12px] text-paper-faint">Named at-risk list needs member-history access</p>
            ) : null}
            <div>
              <h3 className="label mb-2">Reader retention by month</h3>
              {cohorts.isError ? (
                <p className="font-mono text-[12px] text-radar-red">{errorDetail(cohorts.error)}</p>
              ) : !cohorts.data ? (
                <div className="h-24 bg-ink-800 animate-pulse" />
              ) : (
                <HeatGrid
                  ariaLabel="Share of each month's readers who read again in later months"
                  columns={['Readers', '+1 mo', '+2', '+3', '+4', '+5', '+6']}
                  rows={cohorts.data.rows.map((r) => ({ label: r.month, cells: [{ text: fmtInt(r.readers), opacity: 0 }, ...r.retained.map(cohortCell)] }))}
                />
              )}
            </div>
          </div>
        )}
      </Panel>

      {t.sessions === 0 ? (
        <Panel accent="none">
          <p className="text-paper">No visits in this range</p>
          <Link to={`${base}?${new URLSearchParams({ range: presetRange('30d', todayEastern()).preset })}`} className="text-amber-glow hover:underline font-mono text-[12px]">
            Show the last 30 days
          </Link>
        </Panel>
      ) : (
        <>
          <Panel label="Revenue funnel" accent="amber">
            <FunnelBars steps={funnel} />
            <p className="mt-4 text-[11px] text-paper-faint">
              Paid counts card, eCheck, gift and BOGO purchases linked to a visit. PayPal- and Stripe-paid purchases appear in revenue but not yet in the funnel.
            </p>

            <h3 className="label mt-6 mb-2">
              <Link to={`${base}/breakdown/channel?${search}`} className="hover:text-paper">Funnel by channel</Link>
            </h3>
            {channels.isLoading && <div className="h-24 bg-ink-800 animate-pulse" />}
            {channels.data && (
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-[12px] text-paper">
                  <thead>
                    <tr className="border-b border-ink-500">
                      <th className={th}><Link to={`${base}/breakdown/channel?${search}`} className="hover:text-paper">Channel</Link></th>
                      {['Visitors', 'Engaged', 'Considered', 'Checkout', 'Paid', 'Visit → paid'].map((h) => (
                        <th key={h} className={`${th} text-right`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {channels.data.rows.map((r) => (
                      <tr key={r.key} className="border-b border-ink-500/50">
                        <td className="px-3 py-2">
                          <Link to={`${base}/sessions?channel=${encodeURIComponent(r.key)}&${search}`} className="text-amber-glow hover:underline">{r.key}</Link>
                        </td>
                        <td className={tdn}>{fmtInt(r.visitors)}</td>
                        <td className={tdn}>{fmtInt(r.engaged)}</td>
                        <td className={tdn}>{fmtInt(r.considered)}</td>
                        <td className={tdn}>{fmtInt(r.checkout)}</td>
                        <td className={tdn}>{fmtInt(r.paid)}</td>
                        <td className={tdn}>{rate(r.conversion)}</td>
                      </tr>
                    ))}
                    {channels.data.rows.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-4 text-paper-faint">No channels in this range</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel label="Email" accent="violet">
            {emails.isError ? (
              <p className="font-mono text-[12px] text-radar-red">{errorDetail(emails.error)}</p>
            ) : !emails.data ? (
              <div className="h-16 bg-ink-800 animate-pulse" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-[12px] text-paper">
                  <thead>
                    <tr className="border-b border-ink-500">
                      <th className={th}>Campaign</th>
                      {['Emails', 'Visits', 'Human', 'Engaged', 'Paid'].map((h) => (
                        <th key={h} className={`${th} text-right`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {emails.data.rows.slice(0, 8).map((r) => (
                      <tr key={r.campaign} className="border-b border-ink-500/50">
                        <td className="px-3 py-2">
                          <Link to={`${base}/sessions?verdict=all&channel=Email&campaign=${encodeURIComponent(r.campaign)}&${search}`} className="text-amber-glow hover:underline">{r.campaign}</Link>
                        </td>
                        <td className={tdn}>{fmtInt(r.emails)}</td>
                        <td className={tdn}>{fmtInt(r.sessions)}</td>
                        <td className={tdn}>{fmtInt(r.human_sessions)}</td>
                        <td className={tdn}>{fmtInt(r.engaged)}</td>
                        <td className={tdn}>{fmtInt(r.paid)}</td>
                      </tr>
                    ))}
                    {emails.data.rows.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-4 text-paper-faint">No visits from email in this range</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel label="Visitors per day" accent="cyan">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
              {series.isLoading ? (
                <div className="h-44 bg-ink-800 animate-pulse" />
              ) : (
                <LineChart
                  ariaLabel="Visitors per day, this period against the previous period"
                  labels={days.map((d) => d.day.slice(5))}
                  series={[
                    { label: 'This period', values: days.map((d) => d.visitors), tone: 'amber' },
                    { label: 'Previous period', values: prevVals, tone: 'faint', dashed: true },
                  ]}
                />
              )}
              <div className="grid grid-cols-2 gap-4 content-start">
                <Metric size="sm" label="Visitors" value={t.visitors} sub={<span className={TONE_CLASS[vd.tone]}>{vd.text}</span>} />
                <Metric size="sm" label="Visits" value={t.sessions} />
                <Metric size="sm" label="Engaged rate" value={rate(t.engaged_rate)} />
                <Metric size="sm" label="Avg engaged time" value={duration(t.avg_engaged_ms)} />
                <Metric size="sm" label="Pages per visit" value={(Math.round(t.pages_per_session * 10) / 10).toString()} />
                <Metric size="sm" label="Returning" value={rate(t.returning_share)} />
              </div>
            </div>
          </Panel>
        </>
      )}
    </div>
  )
}
